import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import supabase from 'lib/supabase-browser'
import { Submission } from 'lib/types/generated-types'
import { fetchSubmissions } from 'util/db'

export const useSubmissions = ({ projectId, page, perPage }) => {
  const queryClient = useQueryClient()

  return useQuery(
    ['submissions', { page, perPage }],
    () => fetchSubmissions({ projectId, page, perPage }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['project', { id: projectId }])
      },
      refetchInterval: (data) =>
        data?.submissions
          .slice(0, perPage)
          .some(
            (submission) => submission.status?.toLowerCase() !== 'completed'
          )
          ? 5000
          : false,
    }
  )
}

export const DEFAULT_SUBMISSION_FIELDS: (keyof Submission)[] = [
  'id',
  'title',
  'upload_method',
  'filename',
  'created_at',
  's3_file_key',
  's3_audio_key',
  'url',
  'youtube_url',
  'is_speakers_filled_out',
  'is_prompts_filled_out',
  'content_pack_type_id',
  'archived',
  'project_id',
]

export const useSubmission = ({
  id,
  fields = DEFAULT_SUBMISSION_FIELDS,
}: {
  id: string
  fields?: (keyof Submission)[]
}) => {
  return useQuery<Submission | null>(
    ['submission', { id, fields }],

    async () => {
      if (!fields.includes('s3_file_key')) {
        fields.push('s3_file_key')
      }
      const { data, error } = await supabase
        .from('submissions')
        .select(fields.join(','))
        .eq('id', id)
        .eq('archived', false)
        .single()

      if (error) throw new Error(error.message)
      return data as unknown as Submission | null
    },
    {
      enabled: !!id,
      keepPreviousData: true,
      refetchInterval: (data) => (data?.s3_file_key?.length ? false : 5000),
    }
  )
}

export const useBaseSubmission = ({ id }: { id: string }) => {
  return useQuery(
    ['submission', { id }],
    async () => {
      const { data, error } = await supabase
        .from('submissions')
        .select(
          'id, title, upload_method, filename, created_at, s3_file_key, s3_audio_key, url, youtube_url, content_pack_type_id'
        )
        .eq('id', id)
        .eq('archived', false)
        .single()

      if (error) throw error
      return data
    },
    { enabled: !!id }
  )
}

export const useUpdateSubmission = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, data }: { id: string; data: Partial<Submission> }) => {
      const { data: updatedData, error } = await supabase
        .from('submissions')
        .update(data)
        .eq('id', id)
        .select('*')
      if (error) throw error
      return updatedData
    },
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['submission', { id }])
      },
    }
  )
}

export const useDeleteSubmission = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id }: { id: string }) => {
      const { data, error } = await supabase
        .from('submissions')
        .update({
          archived: true,
        })
        .eq('id', id)
        .select('id')
      if (error) throw error
      return data
    },
    {
      onSuccess: (_, { id }) => {
        // Invalidate and refetch queries that could have old data
        Promise.all([
          queryClient.invalidateQueries(['submission', { id }]),
          queryClient.invalidateQueries(['submissions']),
        ])
      },
    }
  )
}

export const useProjectSubmissionsCount = ({
  projectId,
}: {
  projectId: number
}) => {
  return useQuery(
    ['project-submissions-count', { projectId }],
    () => fetchProjectSubmissionsCount({ projectId }),
    { enabled: !!projectId }
  )
}

const fetchProjectSubmissionsCount = async ({
  projectId,
}: {
  projectId: number
}) => {
  const { data, error } = await supabase
    .from('submissions')
    .select('id', { count: 'exact' })
    .eq('project_id', projectId)
  if (error) throw error
  return data
}

export const useCreateSubmission = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (submission: Partial<Submission>) => {
      const { data, error } = await supabase
        .from('submissions')
        .insert(submission)
        .select('*')
        .single()

      if (error) throw error
      return data
    },
    {
      onSuccess: (_, { id }) => {
        Promise.all([
          queryClient.invalidateQueries(['submission', { id }]),
          queryClient.invalidateQueries(['submissions']),
        ])
      },
    }
  )
}
